import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import api from '../services/api';
import { SECTIONS } from '../nucleus/map';

const GroupContext = createContext(null);

const GROUP_REFRESH_MS = 30 * 60 * 1000;

// The group id carried by an address, from either carrier (see the note in the
// provider). Path first — it is the canonical form and outranks the query.
const groupIdFromUrl = (pathname, search) => {
  const match = (pathname || '').match(/\/group\/([a-f0-9-]+)/);
  if (match) return match[1];
  try {
    return new URLSearchParams(search || '').get('group_id') || null;
  } catch {
    return null;
  }
};

// WHICH SPACE YOU STAND IN SURVIVES A REFRESH. The address is still the source
// of truth (see the note in the provider) — but not every page can SAY which
// space you are in. /home, /self and /trash have no group form at all, so a
// reload on one of them read "no group in the address" as "personal" and
// dropped you out of a space you had never left. The last space is remembered
// per user and read back ONLY on a cold boot, and only on a page whose address
// could not have carried a group anyway.
const spaceKeyFor = (userId) => `paradigm_active_space_${userId}`;

// The routes that CAN name a group — a path form (/group/:id/…) or the query
// form (?group_id=). On one of these the address is a full answer: if it names
// no group, personal is a choice, not a gap, so the memory stays shut. Anything
// not listed here cannot express a space, and that is where memory speaks.
const GROUP_BEARING_PATHS = [
  // every section of the map can name a group (/group/<id>/<section>), so on
  // any of them an address with no group means personal (nucleus/map.js)
  ...SECTIONS.map((s) => `/${s.id}`),
  // the old addresses that took ?group_id=
  '/schemas', '/privacy-realms', '/exposure-profiles', '/applications', '/automations', '/manage',
];

const addressCanNameAGroup = (pathname) =>
  GROUP_BEARING_PATHS.includes(`/${(pathname || '').split('/')[1] || ''}`) || (pathname || '').startsWith('/group/');

const readStoredGroupId = (userId) => {
  if (!userId) return null;
  try {
    return localStorage.getItem(spaceKeyFor(userId)) || null;
  } catch {
    return null;
  }
};

const writeStoredGroupId = (userId, groupId) => {
  if (!userId) return;
  try {
    if (groupId) localStorage.setItem(spaceKeyFor(userId), groupId);
    else localStorage.removeItem(spaceKeyFor(userId));
  } catch { /* private mode / quota — the space just will not persist */ }
};

export const GroupProvider = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [groups, setGroups] = useState([]);
  const [groupsLoaded, setGroupsLoaded] = useState(false);

  // WHERE THE GROUP RIDES. The address is the source of truth for which space
  // you stand in — that is what makes a group page linkable and the back button
  // walk spaces. But not every page HAS a group-shaped path: the activity
  // ledger is user-scoped on the server and lives at /activity for personal and
  // group alike. Reading the group from the path alone meant one visit to such
  // a page silently returned you to personal, and every nav item from there on
  // pointed at personal too — you could not get back without the switcher.
  //
  // So the group may ride in EITHER carrier: the path (/group/:id/…, the
  // canonical form) or ?group_id= (for the routes that have no group form).
  // Neither is state hidden from the URL, so nothing about linkability changes.
  const [activeGroupId, setActiveGroupId] = useState(
    () => groupIdFromUrl(window.location.pathname, window.location.search),
  );

  // Sync activeGroupId whenever the URL changes (e.g. navigating between groups).
  // An address that NAMES a group sets it; an address that could have named one
  // and did not means personal. An address that cannot name one either way —
  // /home, /self — is silent, not a vote for personal, so the space you are
  // standing in survives the visit and the index keeps pointing at it.
  useEffect(() => {
    const fromUrl = groupIdFromUrl(location.pathname, location.search);
    if (fromUrl) setActiveGroupId(fromUrl);
    else if (addressCanNameAGroup(location.pathname)) setActiveGroupId(null);
  }, [location.pathname, location.search]);
  const [activeGroup, setActiveGroup] = useState(null);
  const [loading, setLoading] = useState(false);

  // COLD BOOT: fall back to the remembered space when the address names none.
  // `hydrated` gates the writer below so the first render — activeGroupId still
  // null, nothing read yet — cannot erase what we are about to restore. It runs
  // once, when the user is known; every later navigation is the URL's business.
  const hydrated = useRef(false);
  useEffect(() => {
    if (hydrated.current || !user) return;
    hydrated.current = true;
    const { pathname, search } = window.location;
    if (groupIdFromUrl(pathname, search) || addressCanNameAGroup(pathname)) return;
    const stored = readStoredGroupId(user.id);
    if (stored) setActiveGroupId(stored);
  }, [user]);

  useEffect(() => {
    if (!hydrated.current || !user) return;
    writeStoredGroupId(user.id, activeGroupId);
  }, [activeGroupId, user]);

  // `quiet` is the background refresh: no loading flag, and a failure keeps the
  // roster it has. Clearing it would read as "you left every group" and drop
  // you out of the space you are in.
  const fetchGroups = useCallback(async ({ quiet = false } = {}) => {
    if (!user) return;
    if (!quiet) setLoading(true);
    try {
      const data = await api.getGroups();
      setGroups(data.groups || []);
    } catch (err) {
      if (!quiet) setGroups([]);
    } finally {
      setGroupsLoaded(true);
      if (!quiet) setLoading(false);
    }
  }, [user]);

  // Group photo URLs are signed for an hour, so the roster (which carries
  // them) is re-read every half hour.
  useEffect(() => {
    if (!user) return undefined;
    const timer = setInterval(() => fetchGroups({ quiet: true }), GROUP_REFRESH_MS);
    return () => clearInterval(timer);
  }, [user, fetchGroups]);

  // Fetch groups when user changes
  useEffect(() => {
    if (user) {
      fetchGroups();
    } else {
      // No one signed in. On sign-out the address has already left the group;
      // on a cold boot the user is simply not known YET, and the address may
      // name a group (/group/<id>/realms): clearing it here would show the
      // personal space under a group's address, and nothing would bring the
      // group back once the user arrives (the address has not changed). So the
      // space is read from the address, not cleared.
      setGroups([]);
      setGroupsLoaded(false);
      setActiveGroupId(groupIdFromUrl(window.location.pathname, window.location.search));
      setActiveGroup(null);
    }
  }, [user, fetchGroups]);

  // Update activeGroup when activeGroupId changes
  useEffect(() => {
    if (activeGroupId) {
      const group = groups.find(g => g.id === activeGroupId);
      setActiveGroup(group || null);
      // A REMEMBERED SPACE CAN GO STALE — you leave the group, or it is
      // deleted, and the id outlives your membership. Once the roster is in and
      // the address is not the one asking, drop back to personal rather than
      // pointing the whole index at a group that will answer 403.
      if (!group && groupsLoaded && !groupIdFromUrl(location.pathname, location.search)) {
        setActiveGroupId(null);
      }
    } else {
      setActiveGroup(null);
    }
  }, [activeGroupId, groups, groupsLoaded, location.pathname, location.search]);

  const switchToPersonal = useCallback(() => {
    setActiveGroupId(null);
  }, []);

  const switchToGroup = useCallback((groupId) => {
    setActiveGroupId(groupId);
  }, []);

  const value = {
    groups,
    activeGroupId,
    activeGroup,
    loading,
    isGroupContext: !!activeGroupId,
    switchToPersonal,
    switchToGroup,
    refreshGroups: fetchGroups,
  };

  return <GroupContext.Provider value={value}>{children}</GroupContext.Provider>;
};

export const useGroup = () => {
  const context = useContext(GroupContext);
  if (!context) {
    throw new Error('useGroup must be used within GroupProvider');
  }
  return context;
};
